import { useEffect, useState } from 'react';

type AutocompleteSelection = {
  id?: string | null;
  name?: string | null;
};

/**
 * True when autocomplete was seeded with an existing entity (prefill, voucher lock, etc.).
 * Custom "Neu anlegen" entries confirm via {@link useAutocompleteConfirmation}'s callback instead.
 */
export const isAutocompletePrefilled = (selection: AutocompleteSelection): boolean => {
  const name = selection.name?.trim();
  return Boolean(name && selection.id);
};

/**
 * Tracks whether the user confirmed an autocomplete value (picked a suggestion, Enter on one,
 * or "Neu anlegen") — not merely typed while the dropdown is open.
 *
 * Use with {@link AutocompleteField}'s `onConfirmedChange` and gate submit:
 * `disabled={!formState.isValid || !isConfirmed}`.
 */
export const useAutocompleteConfirmation = (selection: AutocompleteSelection) => {
  const { id, name } = selection;
  const [isConfirmed, setIsConfirmed] = useState(() => isAutocompletePrefilled(selection));

  useEffect(() => {
    if (isAutocompletePrefilled({ id, name })) {
      setIsConfirmed(true);
    }
  }, [id, name]);

  return {
    isConfirmed,
    onConfirmedChange: setIsConfirmed,
  };
};
